import type {Inquiry, InquiryStatus, Listing, Profile, Role} from '../store/types';

/**
 * ONLY bridge to the server. No component or hook fetches a URL directly —
 * everything network-related goes through a function exported here, per the
 * fullstack-scaffold rule (see server/, AGENT.md §1.4).
 *
 * Wire format is plain camelCase, matching `store/types.ts` field-for-field —
 * the server's Drizzle schema (server/db/schema.ts) uses the same property
 * names, so there is no case-conversion boundary to keep in sync.
 */

export interface Snapshot {
  listings: Listing[];
  profiles: Profile[];
  inquiries: Inquiry[];
  /** Listing ids the signed-in visitor has saved. */
  saved: string[];
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {'content-type': 'application/json', ...(init?.headers ?? {})},
  });
  const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) {
    throw new Error((data && typeof data.error === 'string' ? data.error : null) ?? `HTTP ${res.status}`);
  }
  return data as T;
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export const authApi = {
  me: () => request<{user: Profile | null}>('/api/auth/me'),
  signUp: (input: {email: string; password: string; name: string; phone: string; role: Role}) =>
    request<{user: Profile}>('/api/auth/signup', {method: 'POST', body: JSON.stringify(input)}),
  signIn: (email: string, password: string) =>
    request<{user: Profile}>('/api/auth/login', {method: 'POST', body: JSON.stringify({email, password})}),
  signOut: () => request<{ok: true}>('/api/auth/logout', {method: 'POST'}),
};

// ── Snapshot — everything the caller may see, in one round trip ──────────────

export const snapshotApi = {
  get: () => request<Snapshot>('/api/snapshot'),
};

// ── Profile ────────────────────────────────────────────────────────────────

export const profileApi = {
  update: (patch: Partial<Pick<Profile, 'name' | 'phone' | 'role'>>) =>
    request<Profile>('/api/profile', {method: 'PATCH', body: JSON.stringify(patch)}),
};

// ── Listings ───────────────────────────────────────────────────────────────

export const listingsApi = {
  /** Create-or-update by id — matches the one `saveListing()` call every screen already makes. */
  upsert: (listing: Listing) => request<Listing>('/api/listings', {method: 'POST', body: JSON.stringify(listing)}),
  remove: (id: string) => request<{ok: true}>(`/api/listings/${id}`, {method: 'DELETE'}),
  incrementViews: (id: string) => request<Listing>(`/api/listings/${id}/view`, {method: 'POST'}),
};

// ── Inquiries ──────────────────────────────────────────────────────────────

export const inquiriesApi = {
  create: (inquiry: Inquiry) => request<Inquiry>('/api/inquiries', {method: 'POST', body: JSON.stringify(inquiry)}),
  setStatus: (id: string, status: InquiryStatus) =>
    request<Inquiry>(`/api/inquiries/${id}`, {method: 'PATCH', body: JSON.stringify({status})}),
};

// ── Saves (favorites) ──────────────────────────────────────────────────────

export const savesApi = {
  set: (listingId: string, on: boolean) =>
    request<{ok: true}>(`/api/saves/${listingId}`, {method: on ? 'POST' : 'DELETE'}),
};

// ── Dev/demo utilities ─────────────────────────────────────────────────────

export const devApi = {
  /** Idempotent — safe to call on every boot, see src/main.tsx. */
  seed: () => request<{ok: true; seeded: boolean}>('/api/dev/seed', {method: 'POST'}),
  /** Wipes listings/inquiries/saves and reseeds — backs "Khôi phục dữ liệu mẫu" in AccountApp. */
  reset: () => request<{ok: true}>('/api/dev/reset', {method: 'POST'}),
};
